import { Injectable } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { FilterState, GetFilterCountsRequestState, GetFilterPresetRequestState, GetFiltersPresetListRequestState, SaveFilterPresetRequestState, SaveFirmIdListPresetRequestState } from "../ngxs/filter/filter.state";
import { BehaviorSubject, combineLatest, map, Observable} from "rxjs";
import { IRequest } from "ngxs-requests-plugin";

import { SaveRestriction, GetFiltersPreset, GetFirmIdListPreset, ResetCurrentRestriction, SetCurrentRestrictionVariable, SetCurrentRestrictionDateArgs, SetCurrentRestrictionDateType, SetCurrentRestrictionPosition, SetCurrentRestrictionValueArgs, SetCurrentRestrictionValueType, SetCurrentRestrictionInvert, GetRestriction, RemoveRestriction, SetFilter, GetFilterCounts, GetFiltersPresetList, SaveFilterPreset, ClearFilter, SetCurrentFilterCountResult, SetFilterCountsSubmittedHash, SaveFirmIdListPreset, GetFirmIdListPresetList, GetFilterCountsStream, ClearFilterState, SetExcludeMetaOnlyFirms } from "../ngxs/filter/filter.actions";
import { IVariable } from "../interfaces/variable.interface";
import { IFilter, IFilterFirmCountResult, IRestriction, IBasicRestriction, IFilterCountRequest, ISetExcludeMetaOnlyFirmsRequest } from "../interfaces/filter.interface";
import { DateRestrictionTypes } from "../interfaces/date-restriction-type.enum";
import { FilterGetterState, FirmIdListGetterState } from "../ngxs/filter/filter-getter.state";
import { IPresetEntity, IPresetReduced } from "../interfaces/presets.interface";
import { StateReset } from "ngxs-reset-plugin";
import { LocalStorageService } from "./local-storage.service";

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  currentFilterCriteriaSubject$: BehaviorSubject<IVariable> = new BehaviorSubject<IVariable>(null);

  @Select(GetFiltersPresetListRequestState)
  getFiltersPresetListRequestState$: Observable<IRequest>;

  @Select(GetFilterPresetRequestState)
  getFilterPresetRequestState$: Observable<IRequest>;

  @Select(SaveFilterPresetRequestState)
  saveFilterPresetRequestState$: Observable<IRequest>;

  @Select(SaveFirmIdListPresetRequestState)
  saveFirmIdListPresetRequestState$: Observable<IRequest>;

  @Select(GetFilterCountsRequestState)
  getFilterCountsRequestState$: Observable<IRequest>;

  @Select(FilterGetterState.getCurrentRestriction)
  currentRestriction$: Observable<IRestriction>;

  @Select(FilterGetterState.getCurrentFilter)
  currentFilter$: Observable<IRestriction[][]>;

  @Select(FilterGetterState.getExcludeMetaOnlyFirms)
  excludeMetaOnlyFirms$: Observable<boolean>;

  @Select(FilterGetterState.getCurrentFilterCounts)
  currentFilterCounts$: Observable<IFilterFirmCountResult[]>;

  @Select(FilterGetterState.getActiveButNotSubmittedFilterCountResult)
  activeButNotSubmittedFilterCountResult$: Observable<number>;

  @Select(FilterGetterState.getActiveButNotSubmittedFilterCountsProgress)
  activeButNotSubmittedFilterCountsProgress$: Observable<number | null>;

  @Select(FilterGetterState.getIsSaveRestrictionDisabled)
  isSaveRestrictionDisabled$: Observable<boolean>;

  @Select(FilterGetterState.getIsCurrentFilterCountLoading)
  isCurrentFilterCountLoading$: Observable<boolean>;

  @Select(FilterGetterState.getLastFilterCount)
  lastFilterCount$: Observable<number>;

  @Select(FilterGetterState.getServerSideEventHashes)
  serverSideEventHashes$: Observable<string[]>;

  // Presets| Filters
  @Select(FilterGetterState.getFiltersPresetList)
  filtersPresetList$: Observable<IPresetReduced[]>;

  @Select(FilterGetterState.getFiltersPreset)
  filtersPreset$: Observable<IPresetEntity>;

  // Presets| Firm Id Lists
  @Select(FirmIdListGetterState.getFirmIdListPresetList)
  firmIdListPresetList$: Observable<IPresetReduced[]>;

  @Select(FirmIdListGetterState.getFirmIdListPreset)
  firmIdListPreset$: Observable<IPresetEntity>;

  unsavedFilterChanges$: Observable<boolean>;

  constructor(
    private store: Store,
    private localStorageService: LocalStorageService) {
      this.localStorageService.excludeMetaOnlyFirmsSubject$.subscribe(exclude => {
        this.store.dispatch(new SetExcludeMetaOnlyFirms({excludeMetaOnlyFirms: exclude, updateFilterCounts: true, trace: ['localStorageService.excludeMetaOnlyFirmsSubject$']}));
      });

      this.unsavedFilterChanges$ = combineLatest([
        this.currentFilter$,
        this.localStorageService.selectedFiltersSubject$,
        this.excludeMetaOnlyFirms$,
        this.localStorageService.excludeMetaOnlyFirmsSubject$
      ]).pipe(
        // Compare current filter and meta info exclusion with localStorage
        map(([stateFilters, storedFilters, stateMetaExclusion, storedMetaExclusion]) => {
          console.debug('stateFilters', stateFilters, '\nstoredFilters', storedFilters);
          
          // Check if meta exclusion has changed
          const metaExclusionChanged = stateMetaExclusion !== storedMetaExclusion;

          // Both null or undefined: no changes in filters
          if (!stateFilters && !storedFilters) {
            console.debug('Both filters null/undefined: check meta exclusion');
            return metaExclusionChanged;
          }

          // One null/undefined, the other not (and not an empty array): changes
          else if (!stateFilters || !storedFilters) {
            console.debug('One filter null/undefined, the other not: changes detected');
            return true;
          }


          // Both not null/undefined: compare deeply
          console.debug('Both filters present: comparing deeply');
          return metaExclusionChanged || !this.filtersAreIdentical(stateFilters, storedFilters);
        })
      );
    }

  getFiltersPresetList() {
    this.store.dispatch(new GetFiltersPresetList());
  }

  getFirmIdListPresetList() {
    this.store.dispatch(new GetFirmIdListPresetList());
  }

  getFiltersPreset(presetName: string) {
    this.store.dispatch(new GetFiltersPreset(presetName));
  }

  getFirmIdListPreset(presetName: string) {
    this.store.dispatch(new GetFirmIdListPreset(presetName));
  }

  saveFiltersPreset(name: string, filters: IRestriction[][], excludeMetaOnlyFirms: boolean) {
    this.store.dispatch(new SaveFilterPreset(name, filters, excludeMetaOnlyFirms));
  }

  saveFirmIdListPreset(name: string, firmIdList: string[]) {
    this.store.dispatch(new SaveFirmIdListPreset(name, firmIdList));
  }

  saveRestriction(editMode?: boolean) {
    this.store.dispatch(new SaveRestriction(editMode));
  }

  getRestriction(i: number, j: number) {
    this.store.dispatch(new GetRestriction({i, j}));
  }

  removeRestriction(i: number, j: number) {
    this.store.dispatch(new RemoveRestriction({i, j}));
  }

  setCurrentRestrictionPosition(i: number, j: number) {
    this.store.dispatch(new SetCurrentRestrictionPosition({i, j}));
  }

  setCurrentRestrictionVariable(variable: IVariable, treeIndex?: number, branchIndex?: number) {
    this.store.dispatch(new SetCurrentRestrictionVariable({filterVariable: variable, treeIndex: treeIndex, branchIndex: branchIndex}));
  }

  setCurrentRestrictionValueType(valueType: string) {
    this.store.dispatch(new SetCurrentRestrictionValueType(valueType));
  }

  setCurrentRestrictionValueArgs(valueArgs: any) {
    this.store.dispatch(new SetCurrentRestrictionValueArgs(valueArgs));
  }

  setCurrentRestrictionDateType(dateType: DateRestrictionTypes) {
    this.store.dispatch(new SetCurrentRestrictionDateType(dateType));
  }

  setCurrentRestrictionDateArgs(years: number[]) {
    this.store.dispatch(new SetCurrentRestrictionDateArgs(years));
  }

  resetCurrentRestriction() {
    this.store.dispatch(new ResetCurrentRestriction());
  }

  setFilter(filter: IFilter) {
    this.store.dispatch(new SetFilter(filter));
  }

  getFilterCounts(filterCountRequest: IFilterCountRequest) {
    this.store.dispatch(new GetFilterCounts(filterCountRequest));
  }

  getFilterCountsStream(hash: string) {
    this.store.dispatch(new GetFilterCountsStream(hash));
  }

  setCurrentFilterCountResult(count: number) {
    this.store.dispatch(new SetCurrentFilterCountResult(count));
  }

  clearFilter() {
    this.store.dispatch(new ClearFilter());
  }

  setFilterCountsSubmittedHash() {
    this.store.dispatch(new SetFilterCountsSubmittedHash());
  }

  setCurrentRestrictionInvert(invert: boolean) {
    this.store.dispatch(new SetCurrentRestrictionInvert(invert));
  }

  setExcludeMetaOnlyFirms(setExcludeMetaOnlyFirmsRequest: ISetExcludeMetaOnlyFirmsRequest) {
    this.store.dispatch(new SetExcludeMetaOnlyFirms(setExcludeMetaOnlyFirmsRequest));
  }

  filtersAreIdentical(filtersA: IRestriction[][] | IBasicRestriction[][], filtersB: IRestriction[][] | IBasicRestriction[][]): boolean {
    // Both null: same
    if (filtersA == null && filtersB == null) {
      return true;
    }

    // One null: not same
    else if (filtersA == null || filtersB == null) {
      return false;
    }

    // Both not null
    // - Length differs: not same
    if (filtersA.length !== filtersB.length) {
      return false;
    }

    // - Check each restriction
    for (let andI = 0; andI < filtersA.length; andI++) {
      for (let orI = 0; orI < filtersA[andI].length; orI++) {
        const filterA = filtersA[andI][orI];
        const filterB = filtersB[andI][orI];

        // Filter value type (e.g. exactmatch)
        if (filterA.filterValueType !== filterB.filterValueType) {
          return false;
        }

        // Filter variable (e.g. LegalForm)
        const filterVariableA = (typeof filterA.filterVariable === 'string') ? filterA.filterVariable : filterA.filterVariable?.tag;
        const filterVariableB = (typeof filterB.filterVariable === 'string') ? filterB.filterVariable : filterB.filterVariable?.tag;
        if (filterVariableA !== filterVariableB) {
          return false
        }
        
        // Filter value (e.g. llc)
        if (JSON.stringify(filterA.filterValue) !== JSON.stringify(filterB.filterValue)) {
          return false;
        }

        // Date restriction type (e.g. mostRecentValue)
        if (filterA.dateRestrictionType !== filterB.dateRestrictionType) {
          return false;
        }

        // Date restriction args (e.g. [2020,2021])
        if (JSON.stringify(filterA.dateRestrictionArgs) !== JSON.stringify(filterB.dateRestrictionArgs)) {
          return false;
        }

        // Invert
        if (filterA.invert !== filterB.invert) {
          return false;
        }
      }
    }

    // All checks passed: same
    console.debug('Filters are identical');
    return true;
  }
}